import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, AlertCircle } from 'lucide-react'
import { EntryToolbar } from './EntryToolbar'
import { useMutation } from '@tanstack/react-query'
import { publishEntry, deleteEvent } from '../../lib/nostr'
import { useAuth } from '../../contexts/AuthContext'

interface EditEntryModalProps {
    isOpen: boolean
    onClose: () => void
    entry: {
        id: string
        content: string
        topicTitle: string
        created_at: string
    }
}

export function EditEntryModal({ isOpen, onClose, entry }: EditEntryModalProps) {
    const { sk, pubkey } = useAuth()
    const [content, setContent] = useState(entry.content)
    const [error, setError] = useState<string | null>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const editMutation = useMutation({
        mutationFn: async () => {
            if (!pubkey) throw new Error('Giriş yapmalısınız')
            if (content.trim().length < 3) throw new Error('Entry en az 3 karakter olmalıdır')
            if (content.trim() === entry.content.trim()) return

            const originalTimestamp = Math.floor(new Date(entry.created_at).getTime() / 1000)

            // Publish new version with original timestamp
            await publishEntry(content.trim(), entry.topicTitle, sk, originalTimestamp)

            // Delete old entry (Kind 5)
            await deleteEvent(entry.id, 'Düzenleme: Yeni sürüm yayınlandı', sk, entry.topicTitle)
        },
        onSuccess: () => {
            onClose()
            // In a real app, subscriptions would update the UI
        },
        onError: (err: any) => {
            setError(err.message || 'Güncelleme sırasında bir hata oluştu')
        }
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        editMutation.mutate()
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="w-full max-w-2xl bg-white dark:bg-surface-900 rounded-2xl shadow-2xl border border-surface-200 dark:border-surface-800 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-800 bg-surface-50/50 dark:bg-surface-800/50">
                            <div>
                                <h3 className="font-poppins font-bold text-lg text-gray-900 dark:text-white">
                                    Entry'yi Düzenle
                                </h3>
                                <p className="text-xs text-gray-500 font-poppins">#{entry.topicTitle}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="space-y-2">
                                <EntryToolbar
                                    textareaRef={textareaRef}
                                    value={content}
                                    onChange={setContent}
                                />

                                <div className="relative">
                                    <textarea
                                        ref={textareaRef}
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        className="w-full p-6 rounded-xl border-2 border-surface-100 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/30 focus:bg-white dark:focus:bg-surface-900 focus:outline-none focus:ring-4 focus:ring-mavi-500/10 focus:border-mavi-500 transition-all resize-none font-merriweather leading-relaxed"
                                        rows={10}
                                        placeholder="Entry'ni burada güncelle..."
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-kirmizi-50 dark:bg-kirmizi-900/20 text-kirmizi-600 dark:text-kirmizi-400 rounded-lg text-sm">
                                    <AlertCircle size={16} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-2 rounded-xl font-poppins text-sm font-medium text-gray-500 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={editMutation.isPending || content === entry.content}
                                    className="btn-primary px-8 py-2 rounded-xl flex items-center gap-2 shadow-lg shadow-mavi-500/20 disabled:opacity-50"
                                >
                                    {editMutation.isPending ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Send size={16} />
                                    )}
                                    <span>Güncelle</span>
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}
